import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CalendarItem, DashboardResponse, ProgressStats } from "@nelurio/contracts";
import { Button, Card, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { Link, useNavigate } from "react-router";
import { api, isDemoMode } from "../api/client";
import { fetchRoutines, type Routine } from "../lib/routines";
import { demoDashboard } from "../demo/data";
import { LAYOUT_EVENT, loadLayout, saveLayout, notifyLayoutChanged, defaultLayout, WIDGET_CATALOG, type WidgetZone, type LayoutItem } from "../widgets/registry";
import type { WidgetEntry } from "../widgets/registry";

const catalogList: WidgetEntry[] = WIDGET_CATALOG;
const dragState: { zone: WidgetZone | ""; index: number } = { zone: "", index: -1 };
import { DashboardDataProvider, DashboardWidget, EmptyStateInline, AcademicWidget, CalendarWidget, CoachWidget, CoursesWidget, FlashcardsWidget, FocusWidget, LibraryWidget, NextExamWidget, PlannerWidget, ProgressWidget, RecordWidget, StatsWidget, TodayWidget, WeeklyFocusWidget, type DashboardContextValue } from "../widgets/DashboardWidgets";

function greetingKey(hour: number) {
  if (hour < 5) return "greeting.night";
  if (hour < 12) return "greeting.morning";
  if (hour < 18) return "greeting.afternoon";
  if (hour < 22) return "greeting.evening";
  return "greeting.night";
}

export function DashboardPage() {
  const { t, formatDate, formatNumber } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardResponse | null>(isDemoMode() ? demoDashboard : null);
  const [stats, setStats] = useState<ProgressStats | null>(null);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [dayData, setDayData] = useState<DashboardResponse | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  // Latest-request token: rapid day clicks must never let an older response
  // overwrite the newly selected day's panel. (Hooks live above every early
  // return — a ref below the error screen crashed with React #310.)
  const dayRequestRef = useRef("");
  // Mirrors selectedDay for listeners registered once (the data-changed effect
  // below) so they can refresh the open day panel without a stale closure.
  const selectedDayRef = useRef<string | null>(null);

  useEffect(() => {
    if (isDemoMode()) return;

    let active = true;

    api<DashboardResponse>(`/dashboard?date=${localDateKey(new Date())}`)
      .then((response) => {
        if (active) {
          setData(response);
          setError("");
        }
      })
      .catch((reason) => {
        if (active) {
          setError(t("common.offline"));
        }
      });

    api<ProgressStats>("/progress").then((response) => {
      if (active) setStats(response);
    }).catch(() => undefined);

    return () => {
      active = false;
    };
  }, [t]);

  // Live counters: refetch when any in-app mutation or window focus happens.
  useEffect(() => {
    if (isDemoMode()) return;
    const refetch = () => {
      api<DashboardResponse>(`/dashboard?date=${localDateKey(new Date())}`).then((response) => { setData(response); setError(""); }).catch(() => undefined);
      api<ProgressStats>("/progress").then(setStats).catch(() => undefined);
      // A day panel open? It must move with every mutation too — after adding
      // a task the selected day used to keep its pre-create content (the
      // calendar dot updated, the panel did not) until the next poll.
      const day = selectedDayRef.current;
      if (day) {
        dayRequestRef.current = day;
        api<DashboardResponse>(`/dashboard?date=${day}`)
          .then((response) => { if (dayRequestRef.current === day) setDayData(response); })
          .catch(() => undefined);
      }
    };
    window.addEventListener("nelurio:data-changed", refetch);
    window.addEventListener("focus", refetch);
    return () => {
      window.removeEventListener("nelurio:data-changed", refetch);
      window.removeEventListener("focus", refetch);
    };
  }, []);

  // Brief API restarts heal themselves: while the outage card stands, quietly
  // re-attempt the dashboard every 5s so recovery is automatic.
  useEffect(() => {
    if (!error) return;
    const timer = window.setInterval(() => {
      api<DashboardResponse>(`/dashboard?date=${localDateKey(new Date())}`).then((response) => { setData(response); setError(""); }).catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [error]);

  function localDateKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  // Today's routines: server list (synced across devices).
  const [serverRoutines, setServerRoutines] = useState<Routine[]>([]);
  useEffect(() => {
    if (isDemoMode()) return;
    fetchRoutines().then((data) => setServerRoutines(data.routines)).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (isDemoMode()) return;
    const refetch = () => fetchRoutines().then((data) => setServerRoutines(data.routines)).catch(() => undefined);
    window.addEventListener("nelurio:data-changed", refetch);
    window.addEventListener("focus", refetch);
    return () => { window.removeEventListener("nelurio:data-changed", refetch); window.removeEventListener("focus", refetch); };
  }, []);
  const routineCountToday = serverRoutines.filter((routine) => Array.isArray(routine.days) && routine.days.includes(new Date().getDay())).length;

  // Dashboard calendar = server items + routine dots (server routines).
  const calendarDots = useMemo(() => {
    const serverItems = Array.isArray(data?.calendar) ? data.calendar : [];
    const routineItems: CalendarItem[] = [];
    const now = new Date();
    const cursor = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const key = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const routines = serverRoutines;
    while (cursor <= end) {
      for (const routine of routines.filter((item) => item.days.includes(cursor.getDay()))) {
        routineItems.push({ id: `rt-${routine.id}-${key(cursor)}`, date: key(cursor), time: routine.time ?? undefined, title: routine.title, type: "study" } as CalendarItem);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return routineItems;
  }, [serverRoutines]);
  const calendar = [...(Array.isArray(data?.calendar) ? data.calendar : []), ...calendarDots];

  const [layout, setLayout] = useState(() => loadLayout());
  useEffect(() => {
    const reload = () => setLayout(loadLayout());
    window.addEventListener(LAYOUT_EVENT, reload);
    return () => window.removeEventListener(LAYOUT_EVENT, reload);
  }, []);
  // iOS-style edit mode lives ON the dashboard (?edit=1 deep-link from Settings).
  const [editing, setEditing] = useState(() => new URLSearchParams(window.location.search).get("edit") === "1");
  const [trayOpen, setTrayOpen] = useState<WidgetZone | null>(null);
  const [dropTarget, setDropTarget] = useState<{ zone: WidgetZone; index: number } | null>(null);
  useEffect(() => {
    if (!editing) return;
    const url = new URL(window.location.href);
    url.searchParams.set("edit", "1");
    window.history.replaceState(null, "", url.toString());
  }, [editing]);
  const enabledIds = useMemo(() => new Set(layout.items.map((item) => item.id)), [layout]);

  // Derived values are null-safe so they can sit above the loading/error
  // early returns together with the hooks that consume them.
  const courses = Array.isArray(data?.courses) ? data.courses : [];
  const scheduled = Array.isArray(data?.scheduled) ? data.scheduled : [];
  const anytime = Array.isArray(data?.anytime) ? data.anytime : [];
  const dueReviews = Number(data?.summary?.dueReviews ?? 0);
  const openTasks = Number(data?.summary?.openTasks ?? 0);
  const focusMinutes = stats ? Math.round(stats.focus.totalSeconds / 60) : 0;
  const focusLabel = focusMinutes >= 60
    ? `${formatNumber(Math.floor(focusMinutes / 60))} ${t("progress.hours")} ${formatNumber(focusMinutes % 60)} ${t("common.minutes")}`
    : `${formatNumber(focusMinutes)} ${t("common.minutes")}`;
  const weekly = stats?.focus.weeks ?? [];
  const maxWeekly = Math.max(1, ...weekly.map((week) => week.seconds));
  // «این هفته» widget data: the CURRENT week's 7 days, seconds per day.
  const weeklyDays = stats?.focus.days ?? [];
  const selectDay = (key: string) => {
    setSelectedDay(key);
    selectedDayRef.current = key;
    if (isDemoMode()) { setDayData(data); setDayLoading(false); return; }
    setDayLoading(true);
    dayRequestRef.current = key;
    api<DashboardResponse>(`/dashboard?date=${key}`)
      .then((response) => { if (dayRequestRef.current === key) { setDayData(response); setDayLoading(false); } })
      .catch(() => { if (dayRequestRef.current === key) { setDayData(null); setDayLoading(false); } });
  };
  const clearDay = () => {
    setSelectedDay(null);
    selectedDayRef.current = null;
    setDayData(null);
    setDayLoading(false);
  };
  // Memoized so poll ticks / unrelated renders do not re-render every widget
  // through a fresh context identity (flashcards fetch keys on ctx.courses).
  const widgetContext: DashboardContextValue = useMemo(() => ({
    // Non-null assertion is honest: the loading/error early returns below run
    // before the provider ever renders this value.
    data: data as DashboardResponse,
    stats,
    courses,
    scheduled,
    anytime,
    dueReviews,
    openTasks,
    routineCountToday,
    focusLabel,
    weekly,
    weeklyDays,
    maxWeekly,
    calendar,
    selectedDay,
    dayData,
    dayLoading,
    selectDay,
    clearDay,
    todayInteractive: enabledIds.has("today"),
  }), [data, stats, courses, scheduled, anytime, dueReviews, openTasks, routineCountToday, focusLabel, weekly, weeklyDays, maxWeekly, calendar, selectedDay, dayData, dayLoading, enabledIds]);

  // NOTE: every hook lives ABOVE this line — the loading/error early returns
  // below must never see a changing hook count (React #310 class).
  if (!data && !error) {
    return <div className="page-loading">{t("common.loading")}</div>;
  }

  if (!data) {
    return (
      <div className="page-error">
        {t("common.offline")}
        <Button onClick={() => { setError(""); window.dispatchEvent(new Event("nelurio:data-changed")); }}>{t("common.retry")}</Button>
      </div>
    );
  }










  const WIDGETS: Record<string, () => React.ReactNode> = {
    today: () => <TodayWidget />,
    calendar: () => <CalendarWidget />,
    stats: () => <StatsWidget />,
    courses: () => <CoursesWidget />,
    focus: () => <FocusWidget />,
    coach: () => <CoachWidget />,
    nextExam: () => <NextExamWidget />,
    weeklyFocus: () => <WeeklyFocusWidget />,
    record: () => <RecordWidget />,
    flashcards: () => <FlashcardsWidget />,
    planner: () => <PlannerWidget />,
    progress: () => <ProgressWidget />,
    academic: () => <AcademicWidget />,
    library: () => <LibraryWidget />,
  };
  const catalog = new Map(catalogList.map((entry) => [entry.id, entry]));
  const zoneItems = (zone: WidgetZone) => layout.items.filter((item) => catalog.get(item.id)?.zone === zone);
  const widgetIndex = (zone: WidgetZone, localIndex: number) => {
    let seen = -1;
    for (let i = 0; i < layout.items.length; i++) {
      if (catalog.get(layout.items[i].id)?.zone !== zone) continue;
      seen++;
      if (seen === localIndex) return i;
    }
    return -1;
  };
  const setLayoutItems = (next: LayoutItem[]) => setLayout({ version: 3, items: next, off: layout.off });
  const moveWidget = (fromZone: WidgetZone, fromLocal: number, toZone: WidgetZone, toLocal: number) => {
    const from = widgetIndex(fromZone, fromLocal);
    const to = widgetIndex(toZone, toLocal);
    if (from === -1 || to === -1 || from === to) return;
    const next = [...layout.items];
    const moved = next.splice(from, 1)[0];
    next.splice(to, 0, moved);
    setLayoutItems(next);
  };
  const resizeWidget = (item: LayoutItem, delta: 1 | -1) => {
    const entry = catalog.get(item.id);
    if (!entry) return;
    const at = entry.sizes.indexOf(item.size);
    const next = entry.sizes[at + delta];
    if (!next) return;
    setLayoutItems(layout.items.map((candidate) => (candidate.id === item.id ? { ...candidate, size: next } : candidate)));
  };
  const removeWidget = (id: string) => {
    setLayoutItems(layout.items.filter((candidate) => candidate.id !== id));
    setLayout((current) => ({ ...current, off: current.off.includes(id) ? current.off : [...current.off, id] }));
  };
  const addWidget = (zone: WidgetZone, id: string) => {
    const entry = catalog.get(id);
    if (!entry) return;
    setLayoutItems([...layout.items, { id, size: entry.defaultSize }]);
    setLayout((current) => ({ ...current, off: current.off.filter((candidate) => candidate !== id) }));
  };
  const exitEdit = () => {
    saveLayout(layout);
    notifyLayoutChanged();
    setEditing(false);
    setTrayOpen(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("edit");
    window.history.replaceState(null, "", url.toString());
  };
  const renderShell = (zone: WidgetZone, item: LayoutItem, localIndex: number) => {
    const render = WIDGETS[item.id];
    if (!render) return null;
    const entry = catalog.get(item.id);
    const isDrop = dropTarget && dropTarget.zone === zone && dropTarget.index === localIndex;
    return (
      <div
        key={item.id}
        className={`dash-widget ${editing ? "is-editing" : ""} ${isDrop ? "is-drop-target" : ""}`}
        data-widget={item.id}
        onDragOver={editing ? (event) => { event.preventDefault(); setDropTarget({ zone, index: localIndex }); } : undefined}
        onDrop={editing ? () => { if (dragState.zone !== "") moveWidget(dragState.zone, dragState.index, zone, localIndex); dragState.zone = ""; setDropTarget(null); } : undefined}
      >
        {editing && entry && (
          <div className="dash-widget__rail">
            <button
              type="button"
              className="dash-widget__chip dash-widget__chip--grip"
              draggable
              onDragStart={() => { dragState.zone = zone; dragState.index = localIndex; }}
              onDragEnd={() => { dragState.zone = ""; setDropTarget(null); }}
              aria-label={t("dbEdit.reorder")}
              title={t("dbEdit.reorder")}
            >
              <Icon name="more" size={14} />
            </button>
            <button
              type="button"
              className="dash-widget__chip"
              disabled={entry.sizes.indexOf(item.size) === 0}
              onClick={() => resizeWidget(item, -1)}
              aria-label={t("dbEdit.sizeS")}
              title={t("dbEdit.sizeS")}
            >
              −
            </button>
            <button
              type="button"
              className="dash-widget__chip"
              disabled={entry.sizes.indexOf(item.size) === entry.sizes.length - 1}
              onClick={() => resizeWidget(item, 1)}
              aria-label={t("dbEdit.sizeL")}
              title={t("dbEdit.sizeL")}
            >
              +
            </button>
            <button
              type="button"
              className="dash-widget__chip"
              disabled={localIndex === 0}
              onClick={() => moveWidget(zone, localIndex, zone, localIndex - 1)}
              aria-label={t("dbEdit.moveUp")}
              title={t("dbEdit.moveUp")}
            >
              ▲
            </button>
            <button
              type="button"
              className="dash-widget__chip"
              disabled={localIndex === zoneItems(zone).length - 1}
              onClick={() => moveWidget(zone, localIndex, zone, localIndex + 1)}
              aria-label={t("dbEdit.moveDown")}
              title={t("dbEdit.moveDown")}
            >
              ▼
            </button>
            <button
              type="button"
              className="dash-widget__chip dash-widget__chip--danger"
              onClick={() => removeWidget(item.id)}
              aria-label={t("common.delete")}
              title={t("common.delete")}
            >
              <Icon name="trash" size={14} />
            </button>
          </div>
        )}
        {render()}
      </div>
    );
  };

  return (
    <div className={`dashboard-layout rise ${editing ? "dashboard-editing" : ""}`}>
      <DashboardDataProvider value={widgetContext}>
        <div className="dashboard-content">
          <PageHeader
            title={`${t(greetingKey(new Date().getHours()))}, ${data.user.displayName}`}
            subtitle={t("dashboard.summary")}
            actions={
              editing ? (
                <button type="button" className="dash-edit-chip dash-edit-chip--done" onClick={exitEdit} aria-label={t("dbEdit.done")}>
                  <Icon name="check" size={13} />
                  <span className="dash-edit-chip__label">{t("dbEdit.done")}</span>
                </button>
              ) : (
                <button type="button" className="dash-edit-chip" onClick={() => setEditing(true)} aria-pressed="false" aria-label={t("dbEdit.title")}>
                  <Icon name="edit" size={13} />
                  <span className="dash-edit-chip__label">{t("dbEdit.title")}</span>
                </button>
              )
            }
          />
          {/* Fixed chrome: the greeting banner stays above whatever widgets the
              owner has enabled — it is the dashboard identity, not a widget. */}
          <Card className="welcome-banner" elevated>
            <div className="welcome-banner__copy">
              <span className="section-kicker">
                {formatDate(new Date(), { weekday: "long", month: "long", day: "numeric" })}
              </span>
              <h2>{t("dashboard.startToday")}</h2>
              <p>
                {formatNumber(dueReviews)} {t(dueReviews === 1 ? "dashboard.dueReviewOne" : "dashboard.dueReviews")} · {formatNumber(openTasks)} {t(openTasks === 1 ? "dashboard.openTaskOne" : "dashboard.openTasks")}
              </p>
              <div className="welcome-banner__actions">
                <Link to="/tasks">
                  <Button icon="play">{t("dashboard.startToday")}</Button>
                </Link>
              </div>
            </div>
            <div className="welcome-banner__path" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
              <span>✦</span>
            </div>
          </Card>
          {zoneItems("main").map((item, localIndex) => renderShell("main", item, localIndex))}
          {editing && (
            <button className="dash-add-cell" onClick={() => setTrayOpen("main")}>
              + {t("dbEdit.addToDash")}
            </button>
          )}
          {!editing && layout.items.length === 0 && (
            <Card className="dashboard-empty-course">
              <EmptyStateInline title={t("dbEdit.emptyTitle")} body={t("dbEdit.emptyBody")} cta={{ to: "/settings?tab=appearance", label: t("dbEdit.title") }} />
            </Card>
          )}
        </div>
        <aside className="dashboard-aside">
          {zoneItems("aside").map((item, localIndex) => renderShell("aside", item, localIndex))}
          {editing && (
            <button className="dash-add-cell" onClick={() => setTrayOpen("aside")}>
              + {t("dbEdit.addToDash")}
            </button>
          )}
        </aside>
      </DashboardDataProvider>
      {editing && trayOpen && createPortal(
        <>
        <div className="dash-tray-backdrop" onClick={() => setTrayOpen(null)} role="presentation" />
        <div className="dash-tray" role="dialog" aria-label={t("dbEdit.disabledGroup")}>
          <div className="dash-tray__head">
            <strong>{t("dbEdit.disabledGroup")}</strong>
            <button type="button" className="dash-tray__close" onClick={() => setTrayOpen(null)} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
            <button type="button" className="dash-tray__reset" onClick={() => setLayout(defaultLayout())}>{t("dbEdit.reset")}</button>
          </div>
          {layout.off.filter((id) => catalog.get(id)?.zone === trayOpen).length === 0 && <p className="dash-tray__empty">{t("dbEdit.trayEmpty")}</p>}
          <div className="dash-tray__list">
            {layout.off.map((id) => {
              const entry = catalog.get(id);
              if (!entry || entry.zone !== trayOpen) return null;
              return (
                <button key={id} type="button" className="dash-tray__item" onClick={() => addWidget(trayOpen, id)}>
                  <Icon name={entry.icon as never} size={17} />
                  <span className="dash-tray__item-label">{t(entry.titleKey)}</span>
                  <Icon name="plus" size={15} className="dash-tray__item-plus" />
                </button>
              );
            })}
          </div>
        </div>
        </>,
        document.body
      )}
    </div>
  );
}
